/**
 * A stable, first-party random id for this browser.
 *
 * It is what separates "unique visits" from "total visits" — the server can
 * count rows without it, but cannot tell one person visiting five times from
 * five people visiting once.
 *
 * Sent as a header on the config request the page already makes, so it adds
 * no network round trip. Strictly first-party: never shared, never used to
 * follow anyone across sites.
 *
 * Every path is wrapped. Safari private mode throws from localStorage, and no
 * analytics counter is worth breaking a page that takes payment.
 */
export const VISITOR_ID_KEY = "aisensy_vid";

/**
 * Cached for the page load so repeated reads don't touch storage. Also the
 * fallback holder when storage is unwritable: the id then lives only for this
 * page load, which still counts the visit — it just can't link it to the
 * visitor's next one.
 */
let cached = null;

const mint = () => {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  // Older Safari has no randomUUID. Uniqueness only needs to hold across
  // this site's visitors, not globally.
  return `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

export const getVisitorId = () => {
  if (cached) return cached;

  try {
    const stored = window.localStorage.getItem(VISITOR_ID_KEY);
    if (stored && stored.trim()) {
      cached = stored;
      return cached;
    }
  } catch {
    // Storage blocked entirely — fall through and mint an in-memory id.
  }

  const fresh = mint();
  cached = fresh;

  try {
    window.localStorage.setItem(VISITOR_ID_KEY, fresh);
  } catch {
    // Private mode. The id stays in `cached` for this page load, so the visit
    // is still recorded; it simply won't be recognised on the next one.
  }

  return fresh;
};

/** Test seam only. */
export const __resetForTests = () => {
  cached = null;
};
